'use client';

import Button from '@/components/ui/Button';
import Dialog from '@/components/ui/Dialog';
import { cn } from '@/lib/utils/utils';
import { Star } from 'lucide-react';
import {
  plusCopy,
  plusFeatures,
  isPlusSelectableFeatureId,
  type PlusSelectableFeatureId,
} from './plusCopy';
import { usePrices } from '../../hooks/usePrices';
import { IPrice } from '@/shared';
import { PlusFeatureCheck } from './PlusFeatureCheck';
import { ZangiaPlusLockIcon } from './ZangiaPlusLockIcon';
import { PlusPackageOption } from './PlusPackageOption';

export function PlusActivateDialog({
  isOpen,
  isEnglish,
  selectedId,
  selectedExtras,
  onSelect,
  onToggleExtra,
  onClose,
  onContinue,
}: {
  isOpen: boolean;
  isEnglish: boolean;
  selectedId: number;
  selectedExtras: PlusSelectableFeatureId[];
  onSelect: (price: IPrice) => void;
  onToggleExtra: (id: PlusSelectableFeatureId) => void;
  onClose: () => void;
  onContinue: () => void;
}) {
  const t = isEnglish ? plusCopy.en : plusCopy.mn;
  const { data } = usePrices(34);
  const prices = data?.data;
  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="xl"
      title={<span className="sr-only">{t.introTitle}</span>}
    >
      <div className="px-1 pb-2 text-center">
        <ZangiaPlusLockIcon variant="badge" />
        <h2 className="text-lg font-semibold text-gray-900">{t.introTitle}</h2>
        <p className="mt-1 text-sm text-gray-500">{t.introSubtitle}</p>
      </div>

      <ul className="divide-y divide-gray-200">
        {plusFeatures.map((feature) => {
          const extraId = isPlusSelectableFeatureId(feature.id)
            ? feature.id
            : null;
          const extraSelected =
            extraId !== null && selectedExtras.includes(extraId);
          const rowClass = cn(
            'flex w-full items-center gap-2.5 py-3',
            extraId !== null && 'px-1'
          );
          const row = (
            <>
              <PlusFeatureCheck
                variant={feature.included ? 'included' : 'checkbox'}
                checked={extraSelected}
              />
              <>
                <span className="min-w-0 flex-1 text-left text-sm text-gray-900">
                  {isEnglish ? feature.en : feature.mn}
                </span>
                <span className="shrink-0 text-sm font-medium text-primary-700">
                  {feature.unlimited ? t.unlimited : feature.count}
                </span>
              </>
            </>
          );

          return (
            <li key={feature.id}>
              {extraId ? (
                <button
                  type="button"
                  onClick={() => onToggleExtra(extraId)}
                  className={rowClass}
                >
                  {row}
                </button>
              ) : (
                <div className={rowClass}>{row}</div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="mt-4 grid grid-cols-2 gap-3">
        {(prices ?? []).map((price) => (
          <PlusPackageOption
            key={price.id}
            price={price}
            selected={price.id === selectedId}
            saleLabel={t.sale}
            onSelect={() => onSelect(price)}
          />
        ))}
      </div>

      <div className="mt-5 flex justify-center">
        <Button
          variant="secondary"
          className="h-10 rounded-full px-3"
          onClick={onContinue}
          disabled={!selectedId}
        >
          <Star className="h-4 w-4" />
          {t.activate}
        </Button>
      </div>
    </Dialog>
  );
}
